import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  useTheme,
  Container 
} from '@mui/material';
import { RefreshRounded, BugReportRounded } from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log error to analytics/monitoring service
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `${error.toString()} - ${errorInfo.componentStack}`,
        fatal: true
      });
    }

    console.error('Error caught by boundary:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return <ErrorFallback 
        error={this.state.error}
        onReload={this.handleReload}
        onGoHome={this.handleGoHome}
      />;
    }

    return this.props.children;
  }
}

function ErrorFallback({ error, onReload, onGoHome }) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'dark' 
          ? 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={6}
          sx={{
            p: 6,
            textAlign: 'center',
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            border: theme.palette.mode === 'dark' 
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.05)',
          }}
        >
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <Box 
              sx={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: '3rem' }}>💸</Typography>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                  fontWeight: 900,
                  color: 'text.primary',
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                }}
              >
                cost.wtf
              </Typography>
            </Box>
          </Box>

          {/* Error Icon */}
          <BugReportRounded 
            sx={{ 
              fontSize: '4rem', 
              color: 'error.main',
              mb: 3 
            }} 
          />

          {/* Error Message */}
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              fontWeight: 700,
              color: 'text.primary',
              mb: 2,
            }}
          >
            Oops! Something went wrong
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              mb: 4,
              fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
            }}
          >
            Don't worry, we're not charging you for this error! 
            <br />
            Try refreshing the page or go back to the homepage.
          </Typography>

          {/* Error Details (Development Only) */}
          {process.env.NODE_ENV === 'development' && error && (
            <Box
              sx={{
                mb: 4,
                p: 3,
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderRadius: 2,
                textAlign: 'left',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                maxHeight: '200px',
                overflow: 'auto',
                border: '1px solid rgba(244, 67, 54, 0.3)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Error Details (Development Mode):
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {error.toString()}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              flexWrap: 'wrap' 
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<RefreshRounded />}
              onClick={onReload}
              sx={{
                px: 4,
                py: 1.5,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              Refresh Page
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={onGoHome}
              sx={{
                px: 4,
                py: 1.5,
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
                fontWeight: 700,
                fontSize: '1.1rem',
              }}
            >
              Go to Homepage
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontFamily: '"Comic Neue", "Comic Sans MS", cursive',
              }}
            >
              If this problem persists, please refresh or try again later.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default ErrorBoundary;